import {
  Pagination as MuiPagination,
  PaginationItem as MuiPaginationItem,
  SxProps,
  Theme,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { LoadingState } from '@insights-ltd/design-library/utils';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Paginator } from '../hooks/usePagination';
import ItemCountLabel from './ItemCountLabel';
import CustomNavigationLabels from './CustomNavigationLabels';
import LoadMoreButton from './LoadMoreButton';

export type ApiPaginationProps = {
  isFetching: boolean;
  hasMoreToLoad: boolean;
  loadMore?: (
    options?: FetchNextPageOptions,
  ) => Promise<
    InfiniteQueryObserverResult<
      { items: unknown[]; totalCount?: number },
      Error
    >
  >;
  loadingState: LoadingState;
};

type PaginationProps = {
  show?: boolean;
  itemsPerPage: number;
  paginator: Paginator<unknown>;
  onPageChange?: (page: { pageNumber: number; lastPage?: boolean }) => void;
  sx?: SxProps<Theme>;
  size?: 'small' | 'medium' | 'large';
  apiPaginationProps?: ApiPaginationProps;
};

export const Pagination = ({
  show = true,
  itemsPerPage,
  paginator,
  onPageChange = () => {},
  sx = {},
  size,
  apiPaginationProps,
}: PaginationProps) => {
  const {
    totalCount,
    pageNumber,
    pageCount,
    handlePageChange: onPaginationChange,
    getPaginationAriaLabel,
  } = paginator;

  const { isFetching, hasMoreToLoad, loadingState, loadMore } =
    apiPaginationProps || {};
  const localTheme = useTheme();
  const { t } = useTranslation();
  const showLoadMoreButton = !!(hasMoreToLoad && loadingState && loadMore);
  const shouldOverrideNext = pageNumber === pageCount && showLoadMoreButton;
  const handlePageChange = (newPageNumber: number) => {
    onPaginationChange(newPageNumber);
    onPageChange({
      pageNumber: newPageNumber,
      lastPage: newPageNumber === pageCount,
    });
  };

  const handleOverrideNext = () => {
    if (loadMore) {
      loadMore().then(() => {
        handlePageChange(pageNumber + 1);
      });
    }
  };

  const labels = {
    prev: t('ui.common.pagination.prev'),
    next: t('ui.common.pagination.next'),
  };
  const labelSx = {
    color: localTheme.palette.primary.main,
    '.Mui-disabled &': {
      color: localTheme.palette.grey[600],
    },
  };

  return show && totalCount > 0 && pageCount > 1 ? (
    <Grid
      container
      wrap="nowrap"
      sx={[
        {
          alignItems: 'baseline',
          justifyContent: 'flex-start',
        },
        (theme) => ({
          marginTop: theme.spacing(spacingSizeMap.S),
          marginBottom: theme.spacing(spacingSizeMap.S),
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <ItemCountLabel
        currentPage={pageNumber}
        totalItems={totalCount}
        itemsPerPage={itemsPerPage}
      />
      <MuiPagination
        data-testid="pagination-controls"
        count={pageCount}
        getItemAriaLabel={getPaginationAriaLabel}
        onChange={(_, page) => handlePageChange(page)}
        page={pageNumber}
        size={size}
        renderItem={(item) => {
          const overrideNext = item.type === 'next' && shouldOverrideNext;
          return (
            <MuiPaginationItem
              {...item}
              sx={{
                '&.Mui-disabled': {
                  opacity: 1,
                },
              }}
              slots={CustomNavigationLabels(labels, labelSx)}
              disabled={overrideNext ? isFetching : item.disabled}
              onClick={(e) => {
                if (overrideNext) {
                  e.preventDefault();
                  handleOverrideNext();
                } else {
                  item.onClick?.(e);
                }
              }}
            />
          );
        }}
      />
      {showLoadMoreButton && (
        <LoadMoreButton
          loadMore={loadMore}
          isFetching={isFetching}
          loadingState={loadingState}
        />
      )}
    </Grid>
  ) : null;
};
